
import { defineComponent, PropType } from 'vue'

import { AutofixFileChooser } from '@/components/RepoIssues'
import { Issue, Maybe, RepositoryIssue } from '~/types/types'

import IntegrationsDetailMixin, { IntegrationShortcodes } from '~/mixins/integrationsDetailMixin'
import RoleAccessMixin from '~/mixins/roleAccessMixin'
import { AppFeatures } from '~/types/permTypes'

export interface CreateIssueActionItem {
  id: string
  icon: string
  label: string
  to?: string
  action?: () => Promise<void>
}

export default defineComponent({
  name: 'IssueActions',
  components: {
    AutofixFileChooser
  },
  mixins: [RoleAccessMixin, IntegrationsDetailMixin],
  props: {
    issue: {
      type: Object as PropType<RepositoryIssue | Issue>,
      required: true
    },
    canCreateAutofix: {
      type: Boolean,
      default: true
    },
    isAutofixEnabled: {
      type: Boolean,
      default: true
    },
    issueCreateIntegrations: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },
  data() {
    return {
      isOpen: false,
      isUpgradeAccountModalOpen: false,
      currentComponent: '',
      isActionLoading: false,
      defaultActionId: ''
    }
  },
  created() {
    this.defaultActionId = this.$cookies.get('ds-default-create-issue-action')
  },
  computed: {
    createIssueOptions(): CreateIssueActionItem[] {
      if (!this.isRepositoryIssue(this.issue)) {
        return []
      }
      const options: CreateIssueActionItem[] = []
      const { provider } = this.$route.params
      const vcsAction = {
        id: 'create-issue-on-vcs',
        icon: provider === 'gl' ? 'gitlab' : 'github',
        label: `Create issue on ${provider === 'gl' ? 'GitLab' : 'GitHub'}`,
        to: this.issue.newVcsIssueUrl || ''
      }

      if (this.isJiraEnabled) {
        options.push({
          id: 'create-issue-on-jira',
          icon: IntegrationShortcodes.JIRA,
          label: 'Create issue on Jira',
          action: this.createJiraIssue
        })
      }

      if (
        this.$gateKeeper.provider(AppFeatures.CREATE_ISSUE_ON_VCS, provider) &&
        this.issue.newVcsIssueUrl
      ) {
        options.push(vcsAction)
      }

      return options
    },
    isJiraEnabled(): boolean {
      return (this.issueCreateIntegrations ?? []).includes(IntegrationShortcodes.JIRA)
    },
    currentOption(): Maybe<CreateIssueActionItem> {
      const userPreferredAction =
        this.defaultActionId ?? this.$cookies.get('ds-default-create-issue-action')

      if (userPreferredAction) {
        const filteredOptions = this.createIssueOptions.filter(
          (opt) => opt.id === userPreferredAction
        )
        if (filteredOptions.length) {
          return filteredOptions[0]
        }
      }

      return this.createIssueOptions.length ? this.createIssueOptions[0] : null
    },
    raisedInFiles(): string[] {
      if (!this.isRepositoryIssue(this.issue)) {
        return []
      }
      const raisedInFiles: Maybe<string>[] = this.issue.raisedInFiles ?? []
      return raisedInFiles.filter((file) => file !== null)
    }
  },
  methods: {
    isRepositoryIssue(issue: RepositoryIssue | Issue): issue is RepositoryIssue {
      return Object.keys(issue).includes('newVcsIssueUrl')
    },
    async createJiraIssue(): Promise<void> {
      this.isActionLoading = true
      try {
        const { ok, issueCode, issueUrl } = await this.createIssueOnIntegration({
          integrationShortcode: IntegrationShortcodes.JIRA,
          repositoryIssueId: this.issue.id
        })

        if (ok && issueUrl) {
          this.$toast.show({
            type: 'success',
            message: `Created issue ${issueCode} on Jira.`,
            timeout: 10,
            primary: {
              label: 'Open issue',
              action: () => {
                window.open(issueUrl, '_blank')
              }
            }
          })
        } else {
          throw Error('Got a non-ok response while creating Jira issue.')
        }
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'There was a problem while creating the issue, please try again later.'
        )
      } finally {
        this.isActionLoading = false
      }
    },
    wrapAction(opt: CreateIssueActionItem | null): Promise<void> | void {
      if (!opt) return
      this.$cookies.set('ds-default-create-issue-action', opt.id)
      this.defaultActionId = opt.id
      if (opt.to) {
        window.open(opt.to, '_blank')
      } else if (opt.action) {
        return opt.action()
      }
    },
    openUpgradeAccountModal(): void {
      this.isOpen = false
      this.isUpgradeAccountModalOpen = true
    }
  }
})
